import { cn } from '@/lib/utils';

export interface SegmentedOption<T extends string> {
  value: T;
  label: string;
}

export interface SegmentedControlProps<T extends string> {
  options: readonly SegmentedOption<T>[];
  value: T;
  onChange: (value: T) => void;
  /** The theme control in the admin screen uses slightly roomier items. */
  size?: 'default' | 'roomy';
  'aria-label': string;
  className?: string;
}

/**
 * Two- or three-way switch with a raised thumb: statistics month/year and the
 * theme preference. Built as a radio group so arrow keys work as expected.
 */
export function SegmentedControl<T extends string>({
  options,
  value,
  onChange,
  size = 'default',
  className,
  'aria-label': ariaLabel,
}: SegmentedControlProps<T>) {
  return (
    <div
      role="radiogroup"
      aria-label={ariaLabel}
      className={cn('inline-flex rounded-control bg-divider p-[3px]', className)}
    >
      {options.map((option) => {
        const selected = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={selected}
            onClick={() => onChange(option.value)}
            className={cn(
              'rounded-cell text-control transition-colors',
              size === 'roomy' ? 'px-4 py-1.5' : 'px-3.5 py-[5px]',
              selected
                ? 'bg-card font-semibold text-fg shadow-thumb'
                : 'font-medium text-fg-muted hover:text-fg-tertiary',
            )}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}
